import { useCallback, useEffect } from "react";
import { UseFormSetValue } from "react-hook-form";
import { useGetSalesUnitByIngredient } from "../../../../api/ingredients";
import { IngredientType } from "../../../../config/enums/IngredientType.enum";
import { SUPPLIER_DEFAULT_COUNTRY_GLOBAL } from "../../../../config/constants";
import { SupplierIngredientDetails } from "../supplierRegister.types";
import { shouldEnableEnvironmentalData } from "../supplierRegister.utils";

interface UseSupplierSalesUnitSyncParams {
  ingredientId?: string;
  supplierId?: string;
  isSupplierExist: boolean;
  setIsSupplierExist: (value: boolean) => void;
  ingredientSuppliers?: Array<{ _id?: string }>;
  selectedSupplierId?: string;
  applyIngredientDetails: (details?: SupplierIngredientDetails) => void;
  setViewEnvironmentalData?: (value: boolean) => void;
  setValue: UseFormSetValue<Record<string, unknown>>;
}

export const useSupplierSalesUnitSync = ({
  ingredientId,
  supplierId,
  isSupplierExist,
  setIsSupplierExist,
  ingredientSuppliers,
  selectedSupplierId,
  applyIngredientDetails,
  setViewEnvironmentalData,
  setValue
}: UseSupplierSalesUnitSyncParams) => {
  const {
    data: salesUnitData,
    isSuccess: isSalesUnitDataSuccess,
    refetch
  } = useGetSalesUnitByIngredient({
    ingredientId,
    supplierId
  });

  const hydrateFromSalesUnit = useCallback(
    (details?: SupplierIngredientDetails) => {
      if (!details) return;

      applyIngredientDetails(details);
      setValue("unit_size", details.unit_size || 0);
      setValue("ean_bar_code", details.ean_bar_code || 0);
      setValue("produceCultivar", details.produceCultivar || 0);
      setValue("product_id", details.product_id || 0);
      setViewEnvironmentalData?.(shouldEnableEnvironmentalData(details));
    },
    [applyIngredientDetails, setValue, setViewEnvironmentalData]
  );

  useEffect(() => {
    if (selectedSupplierId) {
      const exists = ingredientSuppliers?.find(supplier => supplier?._id === selectedSupplierId);
      setIsSupplierExist(!!exists);
    }
  }, [ingredientSuppliers, selectedSupplierId, setIsSupplierExist]);

  useEffect(() => {
    if (!isSupplierExist) return;

    refetch();
    if (isSalesUnitDataSuccess && salesUnitData) {
      hydrateFromSalesUnit({
        ...salesUnitData,
        ingredient_type: salesUnitData.ingredient_type || IngredientType.RAW,
        countryOrigin: salesUnitData.countryOrigin || SUPPLIER_DEFAULT_COUNTRY_GLOBAL
      });
    }
  }, [
    hydrateFromSalesUnit,
    isSalesUnitDataSuccess,
    isSupplierExist,
    refetch,
    salesUnitData
  ]);

  return {
    salesUnitData,
    isSalesUnitDataSuccess
  };
};
